import { useFloatingPosition } from "oolib/dist/utils/customHooks/useFloatingPosition";
import type { Placement, Side } from "@floating-ui/react";

/**
 * Enter/exit offset per side, so the panel always animates out of its trigger:
 * placed below it, the panel slides down into place; placed to its left, in from
 * the right. `side` is resolved AFTER the `flip` middleware runs, so a panel that
 * flips above its trigger animates towards it rather than away from it.
 */
const SIDE_TRANSFORM: Record<Side, string> = {
    top: "translateY(10px)",
    bottom: "translateY(-10px)",
    left: "translateX(10px)",
    right: "translateX(-10px)",
};

interface UseFloatingSetupOptions {
    open: boolean;
    setOpen: (open: boolean) => void;
    placement: Placement;
    mainAxisOffset: number;
    crossAxisOffset: number;
    onHover: boolean;
    enabled: boolean;
    activeIndex: number | null;
    setActiveIndex: (index: number | null) => void;
    listRef: React.MutableRefObject<(HTMLElement | null)[]>;
    shiftBoundary?: Element | null;
}

/**
 * Extracts and configures the floating-ui setup for a Lightbox instance.
 * Wraps oolib's useFloatingPosition with lightbox-specific defaults.
 */
export function useFloatingSetup({
    open,
    setOpen,
    placement,
    mainAxisOffset,
    crossAxisOffset,
    onHover,
    enabled,
    activeIndex,
    setActiveIndex,
    listRef,
    shiftBoundary,
}: UseFloatingSetupOptions) {
    const offsetConfig = { mainAxis: mainAxisOffset, crossAxis: crossAxisOffset };

    const middlewareOptions = {
        offset: { config: offsetConfig },
        // Default boundary is the viewport (clippingAncestors); `shiftBoundary`
        // swaps in a specific element so the panel stops at that element's edge
        // instead. Only `shift` is rebounded — `flip` still measures against the
        // viewport, so a panel with room on screen doesn't flip early just
        // because its own surface is short.
        shift: {
            enabled: true,
            config: shiftBoundary ? { padding: 8, boundary: shiftBoundary } : { padding: 8 },
        },
        flip: { enabled: true },
    };

    const transitionConfig = {
        // 200ms is load-bearing beyond this transition: inline (multilayer) mode
        // gates its framer-motion choreography on the `isMounted` flag that
        // useTransitionStyles derives from this duration. Retuning it here would
        // desync those panels' springs.
        duration: 200,
        initial: ({ side }: { side: Side }) => ({
            opacity: 0,
            transform: SIDE_TRANSFORM[side],
        }),
        // MUST be `none`, not `translate(0)`: any non-none transform makes the
        // panel the containing block for its `position: fixed` descendants. The
        // oolib dropdowns render their options list inline with `position:
        // fixed` + viewport coords (usePopOutOfOverflowHiddenParent — it does
        // NOT portal), so a transform here re-anchors that list to the panel and
        // StyledLightboxPanel's `overflow: hidden` then clips it away entirely.
        // `none` is the identity transform, so the enter/exit tween still
        // interpolates from SIDE_TRANSFORM normally.
        open: { opacity: 1, transform: "none" },
        close: ({ side }: { side: Side }) => ({
            opacity: 0,
            transform: SIDE_TRANSFORM[side],
        }),
    };

    const baseFloatingOptions = {
        open,
        onOpenChange: setOpen,
        placement,
        strategy: "fixed" as const,
        transform: false,
        dismissOptions: { enabled },
        roleOptions: { role: "menu" as const },
        transitionOptions: transitionConfig,
        middlewareOptions,
        listNavigationOptions: {
            enabled,
            listRef,
            activeIndex,
            onNavigate: setActiveIndex,
            loop: true,
            orientation: "vertical" as const,
            focusItemOnOpen: "auto" as const,
            focusItemOnHover: true,
        },
    };

    const floatingOptions = onHover
        ? {
              ...baseFloatingOptions,
              enableTriggerOnClick: false as const,
              enableHover: enabled,
              hoverOptions: {
                  delay: { open: 100, close: 150 },
              },
          }
        : {
              ...baseFloatingOptions,
              enableTriggerOnClick: true as const,
          };

    return useFloatingPosition(floatingOptions);
}
